import { useLocation, Link } from 'react-router-dom';
import { Eye, Home, User, Shield } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Logo } from './Logo';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileModal } from './ProfileModal';

export const BottomNav = () => {
  const location = useLocation();
  const path = location.pathname;
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isChatInputFocused, setIsChatInputFocused] = useState(false); // Web only: track chat input focus
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const initialViewportHeight = useRef<number>(0);
  const { isAuthenticated, userEmail, userId } = useAuth();
  const ADMIN_IDS = ['wYKexhj9scO0No3vhPQvtER7rT23'];
  const isAdmin = ADMIN_IDS.includes(userId || '') || userEmail === 'jacob@nuroleap.com';

  // Initialize viewport height
  useEffect(() => {
    initialViewportHeight.current = window.innerHeight;
  }, []);

  // Detect chat input focus on web MOBILE ONLY - hide nav when chat input is focused (web mobile only)
  useEffect(() => {
    const isCapacitor = (window as any).Capacitor !== undefined;
    const platform = isCapacitor ? (window as any).Capacitor.getPlatform() : 'web';
    
    // Only run on web
    if (platform !== 'web') {
      return;
    }

    const checkMobile = () => window.innerWidth < 768;
    
    // Only run on chat pages
    const chatPaths = ['/chat-onboarding', '/visualization/session', '/books/session'];
    const isChatPage = chatPaths.some(chatPath => path.startsWith(chatPath));
    
    if (!isChatPage) {
      // Reset state when not on a chat page
      setIsChatInputFocused(false);
      return;
    }

    // Listen for focus/blur on textarea elements (chat inputs)
    // This listener runs regardless, but we only set state if mobile
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Check if it's a textarea (likely chat input) AND we're on mobile
      if (target.tagName === 'TEXTAREA' && checkMobile()) {
        setIsChatInputFocused(true);
      } else if (target.tagName === 'TEXTAREA' && !checkMobile()) {
        // Desktop: ensure it's false
        setIsChatInputFocused(false);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // Check if it's a textarea (likely chat input)
      if (target.tagName === 'TEXTAREA') {
        setIsChatInputFocused(false);
      }
    };

    const handleResize = () => {
      // If window resizes to desktop size, reset state
      if (window.innerWidth >= 768) {
        setIsChatInputFocused(false);
      }
    };

    // Always set up listeners, but check mobile status in handlers
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    window.addEventListener('resize', handleResize);

    // Initial check: reset if not mobile
    if (!checkMobile()) {
      setIsChatInputFocused(false);
    }

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      window.removeEventListener('resize', handleResize);
      setIsChatInputFocused(false); // Reset state when cleanup
    };
  }, [path]); // Re-run when path changes

  // Detect keyboard open/close - ONLY on native platforms (not web)
  useEffect(() => {
    const isCapacitor = (window as any).Capacitor !== undefined;
    const platform = isCapacitor ? (window as any).Capacitor.getPlatform() : 'web';
    
    // Skip ALL keyboard detection on web - no keyboard on desktop browsers
    if (platform === 'web') {
      return; // Don't run any keyboard detection on web
    }
    
    // Only run keyboard detection on native platforms (ios/android)
    const handleResize = () => {
      const currentHeight = window.innerHeight;
      const heightDiff = initialViewportHeight.current - currentHeight;
      
      // If viewport shrunk significantly, keyboard is likely open
      if (heightDiff > 150) {
        setIsKeyboardOpen(true);
      } else {
        setIsKeyboardOpen(false);
      }
    };

    const handleFocusIn = () => setIsKeyboardOpen(true);
    const handleFocusOut = () => {
      // Delay hiding to allow for smooth transition
      setTimeout(() => {
        const currentHeight = window.innerHeight;
        const heightDiff = initialViewportHeight.current - currentHeight;
        if (heightDiff <= 150) {
          setIsKeyboardOpen(false);
        }
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // Handle link clicks with debug logging
  const handleLinkClick = (targetPath: string) => {
    console.log('🟡 BottomNav: Link clicked:', targetPath, 'current path:', path);
    // Let React Router handle navigation normally - don't prevent default
  };

  // Hide bottom nav logic:
  // - NEVER hide on desktop web (>= 768px) - always visible regardless of input focus
  // - Hide on native platforms when keyboard is open
  // - Hide on mobile web when chat input is focused
  const isCapacitor = (window as any).Capacitor !== undefined;
  const platform = isCapacitor ? (window as any).Capacitor.getPlatform() : 'web';
  const isDesktopWeb = platform === 'web' && typeof window !== 'undefined' && window.innerWidth >= 768;
  const isMobileWeb = platform === 'web' && typeof window !== 'undefined' && window.innerWidth < 768;
  
  // Desktop web: NEVER hide - skip all hide logic
  if (!isDesktopWeb) {
    // Mobile web or native: check if we should hide
    const shouldHideNav = (platform !== 'web' && isKeyboardOpen) || (isMobileWeb && isChatInputFocused);
    if (shouldHideNav) {
      return null;
    }
  }

  return (
    <>
      {/* Mobile Navigation (< 768px) - Fixed at bottom with current styling */}
      <nav className="md:hidden bg-[#222222] py-4 sm:py-5 w-full fixed bottom-0 z-40 h-20 sm:h-24 flex items-center">
        <div className="w-full max-w-sm sm:max-w-md mx-auto px-4 sm:px-6">
          <div className="flex items-center">
            <Link 
              to="/home"
              onClick={() => handleLinkClick('/home')}
              className={`flex flex-col items-center gap-1 flex-1 text-center text-white ${
                path === '/home' ? 'text-[#5CD2F4]' : 'hover:text-[#5CD2F4] transition-colors'
              }`}
            >
              <Home size={20} className="sm:w-[22px] sm:h-[22px]" />
              <span className="text-[9px] sm:text-[10px]">Home</span>
            </Link>
            <Link 
              to="/visualization"
              onClick={() => handleLinkClick('/visualization')}
              className={`flex flex-col items-center gap-1 flex-1 text-center text-white ${
                path.startsWith('/visualization') ? 'text-[#5CD2F4]' : 'hover:text-[#5CD2F4] transition-colors'
              }`}
            >
              <Eye size={20} className="sm:w-[22px] sm:h-[22px]" />
              <span className="text-[9px] sm:text-[10px]">Visualizations</span>
            </Link>
            <Link 
              to="/onboarding-summary"
              onClick={() => handleLinkClick('/onboarding-summary')}
              className={`flex flex-col items-center gap-1 flex-1 text-center text-white ${
                path === '/onboarding-summary' ? 'text-[#5CD2F4]' : 'hover:text-[#5CD2F4] transition-colors'
              }`}
            >
              <User size={20} className="sm:w-[22px] sm:h-[22px]" />
              <span className="text-[9px] sm:text-[10px]">Your Vision</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Desktop Navigation (>= 768px) - At top of screen, replaces TopBar */}
      <nav className="hidden md:flex backdrop-blur-md bg-black/20 w-full fixed top-0 z-50 h-16 items-center transition-all duration-300">
        <div className="w-full max-w-6xl mx-auto px-4">
          <div className="flex items-center">
            {/* Logo on left - equal width to balance */}
            <div className="flex-1 flex items-center">
              <Logo />
            </div>
            
            {/* Nav links in center */}
            <div className="flex items-center gap-6">
              {/* SVG gradient definitions */}
              <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                  <linearGradient id="navGradientHome" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#79c9cb" />
                    <stop offset="100%" stopColor="#4b7bdd" />
                  </linearGradient>
                  <linearGradient id="navGradientEye" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#79c9cb" />
                    <stop offset="100%" stopColor="#4b7bdd" />
                  </linearGradient>
                  <linearGradient id="navGradientUser" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#79c9cb" />
                    <stop offset="100%" stopColor="#4b7bdd" />
                  </linearGradient>
                  <linearGradient id="navGradientAdmin" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#79c9cb" />
                    <stop offset="100%" stopColor="#4b7bdd" />
                  </linearGradient>
                </defs>
              </svg>
              
              <Link 
                to="/home"
                onClick={() => handleLinkClick('/home')}
                className="flex items-center gap-1.5 transition-colors group"
              >
                {path === '/home' ? (
                  <Home size={18} style={{ stroke: 'url(#navGradientHome)', fill: 'none' }} />
                ) : (
                  <Home size={18} className="text-white group-hover:text-gray-300 transition-colors" />
                )}
                <span className={`text-xs font-medium transition-colors ${
                  path === '/home' 
                    ? 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] bg-clip-text text-transparent' 
                    : 'text-white group-hover:text-gray-300'
                }`}>Home</span>
              </Link>
              <Link 
                to="/visualization"
                onClick={() => handleLinkClick('/visualization')}
                className="flex items-center gap-1.5 transition-colors group"
              >
                {path.startsWith('/visualization') ? (
                  <Eye size={18} style={{ stroke: 'url(#navGradientEye)', fill: 'none' }} />
                ) : (
                  <Eye size={18} className="text-white group-hover:text-gray-300 transition-colors" />
                )}
                <span className={`text-xs font-medium transition-colors ${
                  path.startsWith('/visualization') 
                    ? 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] bg-clip-text text-transparent' 
                    : 'text-white group-hover:text-gray-300'
                }`}>Visualizations</span>
              </Link>
              <Link 
                to="/onboarding-summary"
                onClick={() => handleLinkClick('/onboarding-summary')}
                className="flex items-center gap-1.5 transition-colors group"
              >
                {path === '/onboarding-summary' ? (
                  <User size={18} style={{ stroke: 'url(#navGradientUser)', fill: 'none' }} />
                ) : (
                  <User size={18} className="text-white group-hover:text-gray-300 transition-colors" />
                )}
                <span className={`text-xs font-medium transition-colors ${
                  path === '/onboarding-summary' 
                    ? 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] bg-clip-text text-transparent' 
                    : 'text-white group-hover:text-gray-300'
                }`}>Your Vision</span>
              </Link>
            </div>
            
            {/* User Profile Link on right - equal width to balance */}
            <div className="flex-1 flex items-center justify-end gap-4">
              {isAdmin && (
                <Link
                  to={path.startsWith('/admin') ? '/home' : '/admin'}
                  className="flex items-center gap-1.5 transition-colors group"
                >
                  {path.startsWith('/admin') ? (
                    <Shield size={18} style={{ stroke: 'url(#navGradientAdmin)', fill: 'none' }} />
                  ) : (
                    <Shield size={18} className="text-white group-hover:text-gray-300 transition-colors" />
                  )}
                  <span className={`text-xs font-medium transition-colors ${
                    path.startsWith('/admin')
                      ? 'bg-gradient-to-br from-[#79c9cb] to-[#4b7bdd] bg-clip-text text-transparent'
                      : 'text-white group-hover:text-gray-300'
                  }`}>{path.startsWith('/admin') ? 'Dashboard' : 'Admin'}</span>
                </Link>
              )}
              {isAuthenticated ? (
                <button
                  onClick={() => setIsProfileModalOpen(true)}
                  className="flex items-center gap-1.5 transition-colors group"
                >
                  <User size={18} className="text-white group-hover:text-gray-300 transition-colors" />
                  <span className="text-xs font-medium text-white group-hover:text-gray-300">Profile</span>
                </button>
              ) : (
                <Link 
                  to="/login"
                  className="flex items-center gap-1.5 transition-colors group"
                >
                  <User size={18} className="text-white group-hover:text-gray-300 transition-colors" />
                  <span className="text-xs font-medium text-white group-hover:text-gray-300">Sign In</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
      
      {/* Profile Modal */}
      <ProfileModal 
        isOpen={isProfileModalOpen} 
        onClose={() => setIsProfileModalOpen(false)} 
      />
    </>
  );
};